import type { ReactNode } from 'react';
import { Button } from '../Button/Button';
import { Breadcrumb } from '../Breadcrumb/Breadcrumb';
import type { BreadcrumbItemData } from '../Breadcrumb/Breadcrumb.types';
import './DrawerHeader.css';

/** Drawer header with title/subtitle, optional breadcrumb steps, and action buttons. */
export type DrawerHeaderProps = {
  divider?: boolean;
  stepsBreadcrumb?: boolean;
  breadcrumbItems?: BreadcrumbItemData[];
  showCloseButton?: boolean;
  showPrimaryButton?: boolean;
  title?: string;
  subtitle?: string;
  showSubtitle?: boolean;
  closeLabel?: string;
  primaryLabel?: string;
  className?: string;
  /** Custom actions slot — replaces the built-in Close/Primary buttons. */
  actions?: ReactNode;
  onCloseClick?: () => void;
  onPrimaryClick?: () => void;
};

export function DrawerHeader({
  divider = true,
  stepsBreadcrumb = false,
  breadcrumbItems = [],
  showCloseButton = false,
  showPrimaryButton = true,
  title = 'Long title',
  subtitle = 'Subtitle',
  showSubtitle = true,
  closeLabel = 'Close',
  primaryLabel = 'Next',
  className = '',
  actions: customActions,
  onCloseClick,
  onPrimaryClick,
}: DrawerHeaderProps) {
  const actions = customActions ?? (
    <div className="spark-drawer-header-actions">
      {showCloseButton ? (
        <Button variant="tertiary" size="small" type="button" onClick={onCloseClick}>
          {closeLabel}
        </Button>
      ) : null}
      {showPrimaryButton ? (
        <Button variant="primary" size="small" type="button" onClick={onPrimaryClick}>
          {primaryLabel}
        </Button>
      ) : null}
    </div>
  );

  if (stepsBreadcrumb) {
    return (
      <header
        className={[
          'spark-drawer-header',
          'spark-drawer-header-steps',
          divider ? 'spark-drawer-header-divider' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="spark-drawer-header-steps-row">
          <Breadcrumb
            items={breadcrumbItems}
            type="steps"
            separatorShape="chevron"
            className="spark-drawer-header-breadcrumb"
          />
          {actions}
        </div>
        <div className="spark-drawer-header-copy">
          <h2 className="spark-drawer-header-title">{title}</h2>
          {showSubtitle ? <p className="spark-drawer-header-subtitle">{subtitle}</p> : null}
        </div>
      </header>
    );
  }

  return (
    <header
      className={[
        'spark-drawer-header',
        divider ? 'spark-drawer-header-divider' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="spark-drawer-header-leading">
        <div className="spark-drawer-header-copy">
          <h2 className="spark-drawer-header-title">{title}</h2>
          {showSubtitle ? <p className="spark-drawer-header-subtitle">{subtitle}</p> : null}
        </div>
      </div>
      {actions}
    </header>
  );
}
